import type { ReactNode } from 'react';

export interface PageHeroProps {
  /** Optional uppercase kicker line above the title — e.g. "Sales · Quotes" */
  kicker?: string;
  /** Main page title — 32px bold tracking-tighter */
  title: string;
  /**
   * Optional italic accent word rendered in Instrument Serif after the title.
   * The design renders this as a serif italic phrase — e.g. "for Ceylon Trading."
   */
  titleAccent?: string;
  /** Optional descriptor paragraph below the title */
  dek?: string;
  /** Slot for action buttons rendered flush-right on the same row as the title block */
  actions?: ReactNode;
}

/**
 * PageHero — Tier 1 glass design system component.
 *
 * Renders the standard NV Design System page header with an uppercase kicker,
 * a large tracking-tight title with optional serif italic accent, and a muted
 * descriptor paragraph. Action buttons are slotted on the right.
 *
 * All text content is passed as props — callers are responsible for
 * supplying already-translated strings.
 *
 * Reference: ~/Downloads/quotes.html lines 354-374.
 */
export function PageHero({
  kicker,
  title,
  titleAccent,
  dek,
  actions,
}: PageHeroProps) {
  return (
    <div
      className="flex flex-wrap items-start justify-between gap-4"
      data-testid="page-hero"
    >
      <div>
        {kicker !== undefined && (
          <div
            className="page-hero-kicker"
            style={{
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'var(--accent)',
              fontWeight: 600,
              marginBottom: '0.25rem',
            }}
          >
            {kicker}
          </div>
        )}
        <h1
          className="page-hero-title"
          style={{
            fontSize: '32px',
            fontWeight: 700,
            letterSpacing: '-0.03em',
            lineHeight: 1.02,
            color: 'var(--ink)',
          }}
        >
          {title}
          {titleAccent !== undefined && (
            <span
              className="page-hero-title-accent"
              style={{
                fontFamily: "'Instrument Serif', ui-serif, Georgia, serif",
                fontStyle: 'italic',
                fontWeight: 400,
                color: 'var(--accent)',
                marginLeft: '0.25em',
              }}
            >
              {titleAccent}
            </span>
          )}
        </h1>
        {dek !== undefined && (
          <p
            className="page-hero-dek"
            style={{
              fontSize: '13.5px',
              color: 'var(--ink-muted)',
              marginTop: '0.375rem',
              maxWidth: '36rem',
            }}
          >
            {dek}
          </p>
        )}
      </div>
      {actions !== undefined && (
        <div className="flex items-center gap-2">{actions}</div>
      )}
    </div>
  );
}
